import { Box, HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { FaLinkedinIn } from "react-icons/fa6";
import { LuBriefcase, LuChevronRight, LuLinkedin } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import { useAppDispatch, useAppSelector, useWorkspaceURL } from "../hooks";
import {
  loadLinkedinAdAccounts,
  selectLinkedinAdAccounts,
} from "../store/linkedinSlice";
import { loadIntegrations } from "../store/workspaceSlice";
import { IntegrationStatus } from "../types/integration";

export default function LinkedInAdAccountsPage() {
  const dispatch = useAppDispatch();
  const workspaceURL = useWorkspaceURL();
  const navigate = useNavigate();
  const { integrations } = useAppSelector((s) => s.workspace);
  const adAccounts = useAppSelector(selectLinkedinAdAccounts);
  const isLoadingAdAccounts = useAppSelector(
    (s) => s.linkedin.isLoadingAdAccounts,
  );
  const linkedinIntegration = integrations.find((i) => i.id === "linkedin");
  const isConnected =
    linkedinIntegration?.status === IntegrationStatus.connected;

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "LinkedIn" },
  ]);

  React.useEffect(() => {
    dispatch(loadIntegrations());
  }, [dispatch]);

  React.useEffect(() => {
    if (!isConnected) return;
    dispatch(loadLinkedinAdAccounts());
  }, [dispatch, isConnected]);

  const handleIntegrationSettings = () => {
    navigate(workspaceURL("settings/integrations"));
  };

  if (!isConnected) {
    return (
      <Page>
        <Stack flex="1">
          <PageHeader title="LinkedIn" />
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={<IconCircle Icon={LuLinkedin} size="md" variant="flat" />}
                title="LinkedIn Not Connected"
                description="Connect your LinkedIn account to start managing accounts."
              >
                <Button onClick={handleIntegrationSettings}>
                  Go to Integration Settings
                </Button>
              </EmptyState>
            </HStack>
          </PageBody>
        </Stack>
      </Page>
    );
  }

  return (
    <Page loading={isLoadingAdAccounts}>
      <Stack flex="1" gap="6">
        <PageHeader
          title="LinkedIn"
          subtitle="Ad accounts linked to the connected LinkedIn profile"
        />
        {adAccounts.length === 0 ? (
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={
                  <IconCircle Icon={LuBriefcase} size="md" variant="flat" />
                }
                title="No Accounts"
                description="No LinkedIn accounts found for your organization."
              />
            </HStack>
          </PageBody>
        ) : (
          <Box
            background="white"
            // A real border, not shadow="card"'s inset ring: rows sit inside a
            // border but paint over an inset one once they take a hover background.
            borderColor="border"
            borderRadius="l3"
            borderWidth="1"
            boxShadow="xs"
            maxWidth={"760px" as any}
            overflow="hidden"
            width="full"
          >
            {adAccounts.map((account, index) => (
              <HStack
                key={account.id}
                borderColor="border.soft"
                borderTopWidth={index > 0 ? "1" : "0"}
                cursor={"pointer" as any}
                gap="4"
                onClick={() =>
                  navigate(
                    workspaceURL(
                      `linkedin-ad-accounts/${account.id}/campaigns`,
                    ),
                  )
                }
                paddingX="6"
                paddingY="4"
                _hover={{ background: "bg.subtle" }}
              >
                <HStack
                  background="brand.linkedin"
                  borderRadius="l2"
                  color="white"
                  flexShrink="0"
                  height="8"
                  justify="center"
                  width="8"
                >
                  <FaLinkedinIn size="16" />
                </HStack>
                <Text flex="1" fontSize="sm" fontWeight="medium" truncate>
                  {account.name}
                </Text>
                <Box color="fg.muted" flexShrink="0">
                  <LuChevronRight size="16" />
                </Box>
              </HStack>
            ))}
          </Box>
        )}
      </Stack>
    </Page>
  );
}
